import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import AppHeader from './AppHeader';
import BottomNav from './BottomNav';
import { drumLanguageAtom } from '../store/drumAppAtoms';
import { useDrumMidi } from '../store/MidiContext';

const TITLES: Record<string, (language: string) => string> = {
  library: () => 'Drum',
  'song-detail': (language) => (language === 'ko' ? '난이도 선택' : 'Select Difficulty'),
  cognitive: () => 'Cognitive Activities',
  records: () => 'Records',
  profile: () => 'My Profile',
  results: () => 'Results',
  'sdk-demo': (language) => (language === 'ko' ? 'SDK 데모' : 'SDK Demo'),
  subscription: (language) => (language === 'ko' ? '드럼하이 패스' : 'DRUM Hi Pass'),
  'profile/settings': () => 'Settings',
  'profile/details': () => 'Profile details',
  'profile/notifications': () => 'Notifications',
  notices: () => 'Notices',
  'notice-detail': () => 'Notice details',
  'profile/help': () => 'Help center',
};

const PROFILE_SUBPAGES = ['profile/details', 'profile/notifications', 'notices', 'notice-detail', 'profile/help', 'profile/settings'];
const HIDE_BOTTOM_NAV = ['song-detail', 'results', 'subscription', 'sdk-demo', ...PROFILE_SUBPAGES];

function matchSegment(pathname: string): string {
  const segment = pathname.replace(/^\/drum\/?/, '').replace(/\/$/, '');
  if (segment.startsWith('profile/notices/')) return 'notice-detail';
  return segment;
}

function AppShellLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const language = useAtomValue(drumLanguageAtom);
  const midi = useDrumMidi();
  const segment = matchSegment(location.pathname);

  const hideHeader = segment === 'results';
  const hideBottomNav = HIDE_BOTTOM_NAV.includes(segment);
  const title = TITLES[segment]?.(language) || '';

  const headerBack = (() => {
    if (segment === 'song-detail') return () => navigate('/drum/library');
    if (segment === 'subscription') return () => navigate(-1);
    if (segment === 'sdk-demo') return () => navigate('/drum/profile');
    if (segment === 'notice-detail') return () => navigate('/drum/profile/notices');
    if (segment === 'notices') return () => navigate('/drum/profile');
    if (PROFILE_SUBPAGES.includes(segment)) return () => navigate('/drum/profile');
    return null;
  })();

  const activeNav = segment === 'library' ? 'library' : segment === 'cognitive' ? 'cognitive' : segment === 'records' ? 'records' : '';

  return (
    <main className={`dh-app-shell ${hideBottomNav ? 'without-bottom-nav' : ''} ${hideHeader ? 'without-header' : ''}`}>
      {!hideHeader && (
        <AppHeader
          title={title}
          onBack={headerBack}
          onOpenSubscription={() => navigate('/drum/subscription')}
          onOpenNotices={() => navigate('/drum/profile/notices')}
          onOpenProfile={() => navigate('/drum/profile')}
          onLogoClick={() => navigate('/')}
          language={language}
          midiStatus={midi.status}
          midiDeviceName={midi.deviceName}
          onMidiScan={midi.scan}
        />
      )}
      <div className="app-content" key={location.pathname}>
        <Outlet />
      </div>
      {!hideBottomNav && (
        <BottomNav
          view={activeNav}
          onChange={(next) => navigate(`/drum/${next}`)}
        />
      )}
    </main>
  );
}

export default AppShellLayout;
